import type { LucideIcon } from "lucide-react";

import { leesSamengestelde, type SamengesteldeTegel } from "./samengesteldeTegels";
import {
  AlertTriangle,
  Armchair,
  Brain,
  Brush,
  Building2,
  Bug,
  Cake,
  CalendarDays,
  CalendarRange,
  ChartLine,
  ClipboardList,
  Clock,
  Cpu,
  Cross,
  Euro,
  FileText,
  Hash,
  IdCard,
  Image,
  MessageSquareText,
  Moon,
  Palette,
  PawPrint,
  Pill,
  Receipt,
  Scale,
  StickyNote,
  Stethoscope,
  Syringe,
  Tags,
  Timer,
  Users,
  Utensils,
  Weight,
  Worm,
} from "lucide-react";

/**
 * De tegels van het dieroverzicht, centraal beschreven: hoe ze heten, welke kleur en welk icoon ze
 * hebben, welke onderdelen erin kunnen staan en welke opties ze kennen. De tegels zelf
 * ({@code Tegels.tsx}) lezen dit; het instellingenvenster ook. Zo is er één plek waar een tegel
 * "bestaat".
 *
 * <p>Een gebruiker bepaalt per tegel <em>wat</em> erin staat (welke onderdelen, in welke volgorde — hij
 * sleept ze in een raster) en <em>hoe</em> (de opties, zoals over hoeveel maanden de gewichtsgrafiek
 * loopt). Met "Tegels instellen" kiest hij welke tegels er staan en in welke volgorde. Alles wordt per
 * gebruiker bewaard in de api, als JSON die hier wordt gelezen — defensief: een tegel of onderdeel dat
 * niet meer bestaat valt weg, een nieuwe komt erbij met zijn standaard.
 */

export type TegelSleutel =
  | "basis"
  | "verzorging"
  | "medisch"
  | "gewicht"
  | "bijzonderheden"
  | "fotos"
  | "activiteiten"
  | "verblijf";

/** De zachte kleuren van de icoonrondjes. Elke tegel heeft er één; elk onderdeel ook. */
export type Tint = "grijs" | "accent" | "groen" | "rood" | "oranje" | "amber" | "paars" | "teal";

export interface RegelDef {
  sleutel: string;
  /** i18n-sleutel van de korte naam. */
  label: string;
  icoon: LucideIcon;
  tint: Tint;
  /** Of het onderdeel er standaard in staat. */
  standaard: boolean;
}

export interface OptieDef {
  sleutel: string;
  label: string;
  waarden: { waarde: number | boolean; label: string }[];
  standaard: number | boolean;
}

export interface TegelDef {
  sleutel: TegelSleutel;
  titel: string;
  icoon: LucideIcon;
  tint: Tint;
  regels?: RegelDef[];
  opties?: OptieDef[];
}

const r = (sleutel: string, icoon: LucideIcon, tint: Tint, standaard = true): RegelDef => ({
  sleutel,
  label: `pensionKlant.regel.${sleutel}`,
  icoon,
  tint,
  standaard,
});

export const TEGELS: TegelDef[] = [
  {
    sleutel: "basis",
    titel: "pensionKlant.tegel.basis",
    icoon: Armchair,
    tint: "grijs",
    regels: [
      r("ras", PawPrint, "groen"),
      r("kleur", Palette, "paars"),
      r("geboortedatum", Cake, "oranje"),
      r("geslacht", Users, "accent"),
      r("chipnummer", Cpu, "teal"),
      r("paspoortnummer", IdCard, "accent"),
      r("soort", PawPrint, "groen", false),
      r("vacht", Brush, "amber", false),
      r("registratienummer", Hash, "grijs", false),
      r("dierenarts", Stethoscope, "rood", false),
    ],
  },
  {
    sleutel: "verzorging",
    titel: "pensionKlant.tegel.verzorging",
    icoon: Armchair,
    tint: "oranje",
    regels: [
      r("voeding", Utensils, "rood"),
      r("hoeveelheid", Scale, "oranje"),
      r("schema", Clock, "accent"),
      r("voerBijzonderheden", MessageSquareText, "amber"),
      r("gedrag", Brain, "paars", false),
    ],
  },
  {
    sleutel: "medisch",
    titel: "pensionKlant.tegel.medisch",
    icoon: Cross,
    tint: "rood",
    regels: [
      r("vaccinaties", Syringe, "groen"),
      r("medicatie", Pill, "accent"),
      r("ontworming", Worm, "oranje"),
      r("ontvlooien", Bug, "amber"),
      r("dierenarts", Stethoscope, "rood", false),
    ],
  },
  {
    sleutel: "gewicht",
    titel: "pensionKlant.tegel.gewicht",
    icoon: Timer,
    tint: "accent",
    regels: [r("waarde", Weight, "accent"), r("datum", CalendarDays, "grijs"), r("grafiek", ChartLine, "groen")],
    opties: [
      {
        sleutel: "maanden",
        label: "pensionKlant.optie.maanden",
        waarden: [
          { waarde: 3, label: "pensionKlant.optie.maanden.3" },
          { waarde: 6, label: "pensionKlant.optie.maanden.6" },
          { waarde: 12, label: "pensionKlant.optie.maanden.12" },
        ],
        standaard: 6,
      },
    ],
  },
  {
    sleutel: "bijzonderheden",
    titel: "pensionKlant.tegel.bijzonderheden",
    icoon: AlertTriangle,
    tint: "amber",
    regels: [r("kenmerken", Tags, "amber"), r("notitie", StickyNote, "grijs", false)],
  },
  {
    sleutel: "fotos",
    titel: "pensionKlant.tegel.fotos",
    icoon: Image,
    tint: "paars",
    opties: [
      {
        sleutel: "aantal",
        label: "pensionKlant.optie.aantalFotos",
        waarden: [
          { waarde: 3, label: "3" },
          { waarde: 5, label: "5" },
          { waarde: 7, label: "7" },
        ],
        standaard: 3,
      },
    ],
  },
  {
    sleutel: "activiteiten",
    titel: "pensionKlant.tegel.activiteiten",
    icoon: Clock,
    tint: "teal",
    regels: [
      r("actGewicht", Weight, "accent"),
      r("actMedicatie", Pill, "rood"),
      r("actNotitie", FileText, "accent"),
      r("actLogboek", ClipboardList, "teal"),
    ],
    opties: [
      {
        sleutel: "aantal",
        label: "pensionKlant.optie.aantalActiviteiten",
        waarden: [
          { waarde: 3, label: "3" },
          { waarde: 5, label: "5" },
          { waarde: 8, label: "8" },
        ],
        standaard: 3,
      },
    ],
  },
  {
    sleutel: "verblijf",
    titel: "pensionKlant.tegel.verblijf",
    icoon: CalendarDays,
    tint: "groen",
    regels: [
      r("status", CalendarDays, "groen"),
      r("periode", CalendarRange, "accent"),
      r("kennel", Building2, "paars"),
      r("bezoeknummer", Users, "oranje"),
      r("nachten", Moon, "accent", false),
      r("tarief", Euro, "groen", false),
      r("totaal", Receipt, "teal", false),
    ],
  },
];

export const TEGEL_PER_SLEUTEL: Record<TegelSleutel, TegelDef> = Object.fromEntries(
  TEGELS.map((t) => [t.sleutel, t]),
) as Record<TegelSleutel, TegelDef>;

/** De definitie van één onderdeel van een tegel. */
export function regelDef(tegel: TegelSleutel, sleutel: string): RegelDef | undefined {
  return TEGEL_PER_SLEUTEL[tegel].regels?.find((x) => x.sleutel === sleutel);
}

export interface TegelInstelling {
  sleutel: TegelSleutel;
  zichtbaar: boolean;
  /** De zichtbare onderdelen in hun volgorde; alleen bij tegels met onderdelen. */
  regels?: string[];
  opties?: Record<string, number | boolean>;
}

/** De voorkeur voor het hele overzicht: de tegels in hun volgorde. */
export interface DieroverzichtVoorkeur {
  versie: 1;
  tegels: TegelInstelling[];
  /** Welke velden er op een kaartje in de dierenlijst staan, in deze volgorde. */
  lijst: string[];
  /**
   * De tegels die de gebruiker zelf heeft samengesteld.
   *
   * <p>Ze staan naast de vaste tegels en niet ertussen: een vaste tegel heeft een definitie in de code
   * en een eigen tegel niet, en die twee door elkaar halen betekent dat een naamsverandering in de code
   * iemands zelfgemaakte tegel wist.
   */
  samengesteld: SamengesteldeTegel[];
}

/**
 * De velden die op een kaartje in de dierenlijst kunnen staan. De gebruiker kiest ze met het tandwiel
 * boven de lijst: wie op ras zoekt wil het ras zien, wie chipnummers overtypt wil dat zien.
 */
export const LIJSTVELDEN: RegelDef[] = [
  r("foto", Image, "grijs"),
  r("soort", PawPrint, "groen"),
  r("ras", Tags, "groen"),
  r("geslacht", Users, "accent"),
  r("leeftijd", Cake, "oranje"),
  r("status", CalendarDays, "teal"),
  r("chipnummer", Cpu, "teal", false),
  r("geboortedatum", Cake, "oranje", false),
  r("gecastreerd", Cross, "rood", false),
  r("kenmerken", Tags, "paars", false),
  r("bijzonderheden", AlertTriangle, "amber", false),
  r("dierenarts", Stethoscope, "rood", false),
  r("bezoeknummer", Hash, "grijs", false),
  r("reservering", CalendarRange, "accent", false),
];

export const LIJSTVELD_PER_SLEUTEL: Record<string, RegelDef> = Object.fromEntries(
  LIJSTVELDEN.map((v) => [v.sleutel, v]),
);

export function standaardLijst(): string[] {
  return LIJSTVELDEN.filter((v) => v.standaard).map((v) => v.sleutel);
}

export function standaardInstelling(def: TegelDef): TegelInstelling {
  return {
    sleutel: def.sleutel,
    zichtbaar: true,
    regels: def.regels?.filter((x) => x.standaard).map((x) => x.sleutel),
    opties: Object.fromEntries((def.opties ?? []).map((o) => [o.sleutel, o.standaard])),
  };
}

export function standaardVoorkeur(): DieroverzichtVoorkeur {
  return { versie: 1, tegels: TEGELS.map(standaardInstelling), lijst: standaardLijst(), samengesteld: [] };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Eén bewaarde tegelinstelling, gecontroleerd tegen zijn definitie. */
function leesInstelling(def: TegelDef, ruw: Record<string, unknown>): TegelInstelling {
  const standaard = standaardInstelling(def);
  const bekend = new Set(def.regels?.map((x) => x.sleutel) ?? []);
  const regels = Array.isArray(ruw.regels)
    ? [...new Set(ruw.regels.filter((x): x is string => typeof x === "string" && bekend.has(x)))]
    : standaard.regels;
  const opties: Record<string, number | boolean> = { ...standaard.opties };
  if (isObject(ruw.opties)) {
    for (const o of def.opties ?? []) {
      const w = ruw.opties[o.sleutel];
      if (o.waarden.some((x) => x.waarde === w)) opties[o.sleutel] = w as number | boolean;
    }
  }
  return {
    sleutel: def.sleutel,
    zichtbaar: typeof ruw.zichtbaar === "boolean" ? ruw.zichtbaar : true,
    regels: def.regels ? regels : undefined,
    opties,
  };
}

/**
 * Een bewaarde voorkeur, veilig gemaakt: onbekende tegels vallen weg, dubbele tellen één keer, en
 * tegels die nog niet in de bewaarde lijst stonden komen achteraan met hun standaard.
 */
export function leesVoorkeur(ruw: unknown): DieroverzichtVoorkeur {
  if (!isObject(ruw) || !Array.isArray(ruw.tegels)) return standaardVoorkeur();
  // Een voorkeur van voor de lijstvelden heeft er nog geen; dan gelden de standaardvelden.
  const lijst = Array.isArray(ruw.lijst)
    ? [...new Set(ruw.lijst.filter((x): x is string => typeof x === "string" && x in LIJSTVELD_PER_SLEUTEL))]
    : standaardLijst();
  const gezien = new Set<TegelSleutel>();
  const tegels: TegelInstelling[] = [];
  for (const item of ruw.tegels) {
    if (!isObject(item) || typeof item.sleutel !== "string") continue;
    const def = TEGEL_PER_SLEUTEL[item.sleutel as TegelSleutel];
    if (!def || gezien.has(def.sleutel)) continue;
    gezien.add(def.sleutel);
    tegels.push(leesInstelling(def, item));
  }
  for (const def of TEGELS) {
    if (!gezien.has(def.sleutel)) tegels.push(standaardInstelling(def));
  }
  return { versie: 1, tegels, lijst, samengesteld: leesSamengestelde(ruw.samengesteld) };
}

/** De waarde van een optie, met de standaard als hij niet (goed) is ingesteld. */
export function optie<T extends number | boolean>(inst: TegelInstelling | undefined, sleutel: TegelSleutel, naam: string): T {
  const def = TEGEL_PER_SLEUTEL[sleutel].opties?.find((o) => o.sleutel === naam);
  const w = inst?.opties?.[naam];
  return (w ?? def?.standaard) as T;
}
